/**
 * Lazy Sentry wrapper — provides fire-and-forget access to Sentry APIs
 * via dynamic import() so the SDK stays out of the main bundle.
 *
 * Imports from @sentry/core (not @sentry/nuxt or @sentry/browser) to avoid
 * pulling in @sentry-internal/replay, feedback, and replay-canvas (~150 KiB).
 * Core functions work via the global hub initialised by sentry.client.config.ts.
 *
 * Type-only imports are erased at build time and do NOT affect the bundle.
 */
import type { Breadcrumb, CaptureContext, SeverityLevel } from '@sentry/core'

let sentryPromise: Promise<typeof import('@sentry/core')> | null = null

/**
 * Returns a cached promise that resolves to the @sentry/core module.
 * Uses @sentry/core instead of @sentry/nuxt to avoid pulling in
 * @sentry-internal/replay, feedback, and replay-canvas (~150 KiB).
 * Core functions work via the global hub initialised by sentry.client.config.ts.
 */
export function getSentry(): Promise<typeof import('@sentry/core')> {
  if (!sentryPromise) {
    sentryPromise = import('@sentry/core')
  }
  return sentryPromise
}

// Fire-and-forget wrappers for use in sync contexts
// (MutationObserver callbacks, error event listeners, etc.)

export function lazyAddBreadcrumb(breadcrumb: Breadcrumb): void {
  getSentry()
    .then((s) => s.addBreadcrumb(breadcrumb))
    .catch(() => {})
}

export function lazySetContext(
  name: string,
  context: Record<string, unknown> | null,
): void {
  getSentry()
    .then((s) => s.setContext(name, context))
    .catch(() => {})
}

export function lazySetTag(key: string, value: string): void {
  getSentry()
    .then((s) => s.setTag(key, value))
    .catch(() => {})
}

export function lazySetExtra(key: string, extra: unknown): void {
  getSentry()
    .then((s) => s.setExtra(key, extra))
    .catch(() => {})
}

export function lazyCaptureException(
  error: unknown,
  hint?: CaptureContext,
): void {
  getSentry()
    .then((s) => s.captureException(error, hint))
    .catch(() => {})
}

export function lazyCaptureMessage(
  message: string,
  level?: SeverityLevel,
): void {
  getSentry()
    .then((s) => s.captureMessage(message, level))
    .catch(() => {})
}
